import React from 'react';
import { Link } from 'react-router-dom';
import SEO, { buildLocalBusinessSchema } from './SEO';
import { business, serviceAreas } from './siteData';
import {
  AboutCTA,
  AboutContent,
  AboutHero,
  AboutTextGrid,
  AboutWrapper,
  GalleryIntro,
  GalleryItem,
  GalleryWrapper,
  ServiceAreaPanel,
} from './styled/about.style';
import ImageGallery from 'react-image-gallery';
import 'react-image-gallery/styles/css/image-gallery.css';
import comm1 from './images/comm1.jpg';
import comm2 from './images/comm2.jpg';
import comm3 from './images/comm3.jpg';
import comm4 from './images/comm4.jpg';
import comm5 from './images/comm5.jpg';
import grid1 from './images/grid1.jpg';
import grid2 from './images/grid2.jpg';
import grid3 from './images/grid3.jpg';
import res1 from './images/res1.jpg';
import res2 from './images/res2.jpg';
import res3 from './images/res3.jpg';
import res4 from './images/res4.jpg';
import res5 from './images/res5.jpg';
import res6 from './images/res6.jpg';
import steel1 from './images/steel1.jpg';

const makeGalleryItem = (image, alt) => ({
  original: image,
  thumbnail: image,
  originalAlt: alt,
  thumbnailAlt: alt,
});

const galleryGroups = [
  {
    title: 'Residential Drywall',
    description:
      'Drywall installation, taping, finishing, and repair for homes, additions, remodels, and basements.',
    images: [
      makeGalleryItem(res1, 'Residential drywall room with purple board and taped seams'),
      makeGalleryItem(res2, 'Residential drywall installation progress in a Western New York home'),
      makeGalleryItem(res3, 'Residential drywall finishing work with clean wall and ceiling seams'),
      makeGalleryItem(res4, 'Residential drywall and ceiling finish in a remodel project'),
      makeGalleryItem(res5, 'Finished residential drywall detail by KMK Drywall'),
      makeGalleryItem(res6, 'Drywall finishing and repair work in a residential room'),
    ],
  },
  {
    title: 'Commercial Drywall',
    description:
      'Large-format drywall installation and finishing for offices, retail spaces, and commercial interiors.',
    images: [
      makeGalleryItem(comm1, 'Commercial drywall installation in a large interior space'),
      makeGalleryItem(comm2, 'Commercial drywall finishing and metal stud work in progress'),
      makeGalleryItem(comm3, 'Commercial drywall project with finished walls and ceilings'),
      makeGalleryItem(comm4, 'Commercial drywall and steel framing project underway'),
      makeGalleryItem(comm5, 'Commercial interior drywall finish by KMK Drywall'),
    ],
  },
  {
    title: 'Ceiling Tile Grid',
    description:
      'Acoustical ceiling grid and tile installation for clean, accessible commercial ceilings.',
    images: [
      makeGalleryItem(grid1, 'Finished acoustical ceiling tile grid in a commercial interior'),
      makeGalleryItem(grid2, 'Commercial ceiling grid and tile installation in progress'),
      makeGalleryItem(grid3, 'Ceiling tile grid installation with lighting panels'),
    ],
  },
  {
    title: 'Steel Framing',
    description:
      'Metal stud framing that creates straight, accurate walls and partitions for commercial build-outs.',
    images: [
      makeGalleryItem(steel1, 'Steel framing installation for a Western New York commercial project'),
    ],
  },
];

const About = () => {
  return (
    <AboutWrapper>
      <SEO
        title="About KMK Drywall | Buffalo, Erie County, & Western New York"
        description="Learn about KMK Drywall, a family-owned drywall contractor based in Springville, NY and serving Buffalo, Erie County, and Western New York."
        path="/about"
        schema={buildLocalBusinessSchema('/about')}
      />
      <AboutHero>
        <p>About KMK Drywall</p>
        <h1>Family-owned drywall contractor based in Springville, NY.</h1>
        <span>
          KMK Drywall provides residential drywall, commercial drywall, drywall
          finishing, steel framing, acoustical ceiling grid, and drywall repair
          throughout {business.serviceArea}.
        </span>
      </AboutHero>
      <AboutContent>
        <AboutTextGrid>
          <section>
            <h2>Drywall work with a local name behind it.</h2>
            <p>
              KMK Drywall is a family-owned, fully insured drywall company serving
              homeowners, contractors, and commercial property teams across Western
              New York. The team handles the interior work that needs to be straight,
              clean, and ready for the next trade: hanging drywall, finishing seams,
              framing commercial spaces, repairing damaged walls, and installing
              ceiling tile grid.
            </p>
            <p>
              Whether you are renovating a room near Springville, building out a
              commercial interior in Buffalo, or coordinating a project elsewhere
              in Erie County, KMK Drywall focuses on dependable communication and
              professional jobsite results.
            </p>
          </section>
          <ServiceAreaPanel>
            <h2>Local service area</h2>
            <p>
              KMK Drywall serves drywall customers throughout Western New York,
              including:
            </p>
            <ul>
              {serviceAreas.map((area) => (
                <li key={area}>{area}</li>
              ))}
            </ul>
            <a href={business.phoneHref}>Call {business.phoneDisplay}</a>
          </ServiceAreaPanel>
        </AboutTextGrid>

        <GalleryIntro>
          <p>Project gallery</p>
          <h2>Residential, commercial, framing, and ceiling work.</h2>
          <span>
            These photos show the range of KMK Drywall projects, from detailed
            residential drywall finishing to larger commercial interiors with steel
            framing and acoustical ceiling systems.
          </span>
        </GalleryIntro>
        <GalleryWrapper>
          {galleryGroups.map((gallery) => (
            <GalleryItem as="article" key={gallery.title}>
              <div>
                <h3>{gallery.title}</h3>
                <p>{gallery.description}</p>
              </div>
              <ImageGallery
                items={gallery.images}
                lazyLoad
                showPlayButton={false}
                showFullscreenButton={false}
              />
            </GalleryItem>
          ))}
        </GalleryWrapper>

        <AboutCTA>
          <h2>Have a project in Buffalo, Erie County, or Western New York?</h2>
          <p>
            Send the KMK Drywall team the project location, a short description,
            and any photos that help explain the work.
          </p>
          <Link to="/quote">Request a Quote</Link>
        </AboutCTA>
      </AboutContent>
    </AboutWrapper>
  );
};

export default About;
